import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { STORY_VERBS, spineCurrentFromLocation, spineNeighborsAllowed, withApp } from "../lib/story";
import { navItemAllowed, showStorySpine, storySpinePager } from "../lib/master";
import { useGuide } from "../lib/guide";

export default function StorySpine({ current, compact = false, appId, interactive = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const guide = useGuide();
  const visible = showStorySpine(guide);
  const pager = storySpinePager(guide);
  const selected = appId || new URLSearchParams(location.search).get("app");
  const active = spineCurrentFromLocation(location.pathname, location.search) || current || null;
  const { prev, next } = spineNeighborsAllowed(location.pathname, location.search, guide);
  const verbs = STORY_VERBS.filter((verb) => navItemAllowed(verb.route, guide));

  const go = (route) => {
    if (!interactive || !route) return;
    if (!navItemAllowed(route, guide)) return;
    navigate(withApp(route, selected));
  };

  useEffect(() => {
    if (!visible) return undefined;
    const params = new URLSearchParams(location.search);
    const anchor = params.get("step") || params.get("focus");
    if (!anchor) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`spine-${anchor}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search, visible]);

  if (!visible) return null;

  return (
    <div className={`story-spine ${compact ? "compact" : ""}`} data-testid="story-spine">
      {!compact && (
        <div className="story-spine-legend">
          <span className="alt-now">Now</span>
          <span className="alt-lean">Lean now</span>
          <span className="alt-next">Next</span>
        </div>
      )}
      <div className="story-spine-row">
        {verbs.map((verb, i) => (
          <button
            type="button"
            key={verb.id}
            className={`story-verb alt-${verb.altitude} ${active === verb.id ? "is-current" : ""}`}
            data-testid={`story-verb-${verb.id}`}
            title={`${verb.label}: ${verb.note}`}
            aria-current={active === verb.id ? "step" : undefined}
            onClick={() => go(verb.route)}
            disabled={!interactive}
          >
            <small>{String(i + 1).padStart(2, "0")}</small>
            <strong>{verb.label}</strong>
            <em>{verb.note}</em>
          </button>
        ))}
      </div>
      {interactive && pager && (
        <div className="spine-pager" data-testid="spine-pager">
          <button
            type="button"
            className="secondary-button"
            data-testid="spine-back"
            onClick={() => prev && go(prev.route)}
          >
            <ChevronLeft size={16} />
            {prev ? `Back · ${prev.label}` : "Back"}
          </button>
          <button
            type="button"
            className="primary-button"
            data-testid="spine-forward"
            onClick={() => next && go(next.route)}
          >
            {next ? `Next · ${next.label}` : "Next"}
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
